import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Truck, Store, ArrowRight, ArrowLeft } from "lucide-react";
import Logo from "../components/portal/Logo";
import api from "@/lib/api";
import { deriveInvoiceGroup } from "@/lib/invoice";

/**
 * Group B landing page — customers with valid 2000–2999 invoices land here.
 *
 * Kept as a fully independent file from GroupALanding so its copy,
 * questions, or flow can diverge without touching Group A. The current
 * content mirrors the existing fulfilment chooser — it will be
 * replaced when Group B-specific requirements arrive.
 */
const cardBase =
  "text-left w-full rounded-3xl border p-6 sm:p-8 transition-all duration-300 group";

export default function GroupBLanding() {
  const navigate = useNavigate();
  const location = useLocation();
  const invoiceNumber = location.state?.invoice_number || "";
  const groupType = deriveInvoiceGroup(invoiceNumber);
  // Server-driven price gate — the ₹300 badge on Home Delivery is
  // rendered only for invoices that staff has explicitly flagged as
  // chargeable. Never inferred from group_type alone.
  const [chargeable, setChargeable] = useState(false);
  const [amountPaise, setAmountPaise] = useState(30000);

  useEffect(() => {
    if (groupType !== "B") navigate("/invoice", { replace: true });
  }, [groupType, navigate]);

  useEffect(() => {
    if (groupType !== "B") return;
    let cancelled = false;
    api.get(`/invoice/${encodeURIComponent(invoiceNumber)}/payment-required`)
      .then(({ data }) => {
        if (cancelled) return;
        setChargeable(!!data.payment_required);
        if (data.amount_paise) setAmountPaise(data.amount_paise);
      })
      .catch(() => { if (!cancelled) setChargeable(false); });
    return () => { cancelled = true; };
  }, [groupType, invoiceNumber]);

  if (groupType !== "B") return null;

  const goTo = (path) =>
    navigate(path, { state: { invoice_number: invoiceNumber, group_type: groupType } });

  return (
    <div className="min-h-screen bg-ivory" data-testid="group-b-landing">
      <header className="border-b border-line bg-ivory/70 backdrop-blur">
        <div className="max-w-4xl mx-auto flex items-center justify-between px-5 sm:px-8 py-5">
          <Logo />
          <button
            data-testid="back-home-btn"
            onClick={() => navigate("/invoice")}
            className="text-xs text-muted2 hover:text-maroon inline-flex items-center gap-1.5 transition-colors"
          >
            <ArrowLeft size={13} strokeWidth={1.6} /> Back
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-5 sm:px-8 py-10 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-10 sm:mb-14"
        >
          <p className="overline text-[10px] mb-3">A little courtesy</p>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-ink leading-[1.15]">
            How would you like to receive your order?
          </h1>
          <p className="text-sm sm:text-base text-muted2 mt-4 max-w-xl mx-auto leading-relaxed">
            Please select your preferred option below.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
          <motion.button
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -3 }}
            data-testid="choose-store-pickup"
            onClick={() => goTo("/pickup")}
            className={`${cardBase} bg-white border-line hover:border-maroon hover:shadow-[0_20px_60px_-30px_rgba(106,31,43,0.35)]`}
          >
            <div className="flex items-start justify-between mb-6">
              <div className="h-12 w-12 rounded-2xl bg-maroon/[0.06] text-maroon flex items-center justify-center">
                <Store size={22} strokeWidth={1.5} />
              </div>
              <span className="overline text-[10px] text-muted2">Option 1</span>
            </div>
            <h2 className="font-serif text-2xl text-ink leading-tight">In-Store Pickup</h2>
            <p className="text-sm text-muted2 mt-3 leading-relaxed min-h-[3.5rem]">
              Prefer to collect your outfit from our store? Schedule a convenient pickup time and visit us in person.
            </p>
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-maroon font-medium group-hover:gap-3 transition-all">
              Schedule In-Store Pickup
              <ArrowRight size={15} strokeWidth={1.8} />
            </div>
          </motion.button>

          <motion.button
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
            whileHover={{ y: -3 }}
            data-testid="choose-home-delivery"
            onClick={() => goTo("/delivery")}
            className={`${cardBase} bg-white border-line hover:border-maroon hover:shadow-[0_20px_60px_-30px_rgba(106,31,43,0.35)]`}
          >
            <div className="flex items-start justify-between mb-6">
              <div className="h-12 w-12 rounded-2xl bg-maroon/[0.06] text-maroon flex items-center justify-center">
                <Truck size={22} strokeWidth={1.5} />
              </div>
              <span className="overline text-[10px] text-muted2">Option 2</span>
            </div>
            <div className="flex items-baseline justify-between gap-2">
              <h2 className="font-serif text-2xl text-ink leading-tight">Home Delivery</h2>
              {chargeable && (
                <span
                  data-testid="group-b-delivery-price"
                  className="inline-flex items-center rounded-full bg-maroon/[0.08] border border-maroon/20 text-maroon text-xs font-medium px-2.5 py-1"
                >
                  ₹{Math.round((amountPaise || 30000) / 100)}
                </span>
              )}
            </div>
            <p className="text-sm text-muted2 mt-3 leading-relaxed min-h-[3.5rem]">
              Have your finished outfit delivered to your preferred address.
              {chargeable && (
                <>
                  <br />
                  <span className="text-ink font-medium">
                    Delivery charge: ₹{Math.round((amountPaise || 30000) / 100)}
                  </span>
                  <span className="block text-[11px] text-muted2 mt-1 italic">
                    Paid at checkout · secure payment via Razorpay
                  </span>
                </>
              )}
            </p>
            <div className="mt-6 inline-flex items-center gap-2 text-sm text-maroon font-medium group-hover:gap-3 transition-all">
              {chargeable ? "Continue with Paid Home Delivery" : "Continue with Home Delivery"}
              <ArrowRight size={15} strokeWidth={1.8} />
            </div>
          </motion.button>
        </div>
      </main>
    </div>
  );
}
